import { useCallback, useEffect, useRef, useState } from 'react'

export type VoiceRecorderState = 'idle' | 'requesting' | 'recording'

// First supported wins: webm/opus (Chrome/Firefox), mp4 (iOS Safari). The server
// accepts any audio/* (video/webm included), so an empty string (browser default) is fine too.
const pickMimeType = () =>
  ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find(
    (type) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type),
  ) ?? ''

type UseVoiceRecorderOptions = {
  /** Fired with the final blob after stop(). */
  onRecorded: (blob: Blob) => void
  onPermissionDenied: () => void
}

export const useVoiceRecorder = ({ onRecorded, onPermissionDenied }: UseVoiceRecorderOptions) => {
  const [state, setState] = useState<VoiceRecorderState>('idle')
  const [elapsed, setElapsed] = useState(0)
  const [stream, setStream] = useState<MediaStream | null>(null)

  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const discardRef = useRef(false)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const onRecordedRef = useRef(onRecorded)
  onRecordedRef.current = onRecorded
  const onPermissionDeniedRef = useRef(onPermissionDenied)
  onPermissionDeniedRef.current = onPermissionDenied

  const teardown = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    recorderRef.current?.stream.getTracks().forEach((track) => track.stop())
    recorderRef.current = null
    setStream(null)
    setElapsed(0)
    setState('idle')
  }, [])

  const start = useCallback(async () => {
    if (recorderRef.current) return
    setState('requesting')
    let mediaStream: MediaStream
    try {
      mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      setState('idle')
      onPermissionDeniedRef.current()
      return
    }

    const mimeType = pickMimeType()
    const recorder = new MediaRecorder(mediaStream, mimeType ? { mimeType } : undefined)
    recorder.onstart = () => setState('recording')
    chunksRef.current = []
    discardRef.current = false
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data)
    }
    recorder.onstop = () => {
      const discarded = discardRef.current
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' })
      chunksRef.current = []
      teardown()
      if (!discarded && blob.size > 0) {
        onRecordedRef.current(blob)
      }
    }

    recorderRef.current = recorder
    setStream(mediaStream)
    setElapsed(0)
    timerRef.current = setInterval(() => setElapsed((seconds) => seconds + 1), 1000)
    recorder.start()
  }, [teardown])

  const stop = useCallback(() => {
    recorderRef.current?.stop()
  }, [])

  const cancel = useCallback(() => {
    discardRef.current = true
    if (recorderRef.current) recorderRef.current.stop()
    else teardown()
  }, [teardown])

  // Unmount while recording: release the mic.
  useEffect(
    () => () => {
      discardRef.current = true
      recorderRef.current?.stop()
    },
    [],
  )

  return { state, elapsed, stream, start, stop, cancel }
}
